import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Price Tiers (PART 5, MD-14 — added by owner ruling R5).
 *
 * A tenant-defined LIST of price bands: any number, tenant-named, in any
 * language. A tier is a LABEL item prices hang off — no percentage, no
 * formula, no currency (a discounting price list is MD-05, not this).
 * Nothing anywhere may assume three tiers or the seeded names
 * (BR-MD-14-02/-10).
 *
 * Permission area: `inventory` — PART 16.2.2 row 3; the tiers screen lives
 * under Inventory & Warehouses (MD-14 §3). Matrix 16.4.2: Administrator
 * RCUDA, Accountant R, AP Clerk R.
 *
 * ── MD-05 IS DEFERRED BY THE SPECIFICATION, AND THIS IS WHY IT IS RECORDED
 *    HERE RATHER THAN LEFT LOOKING LIKE UNBUILT WORK ─────────────────────────
 *
 * MD-05 "Price handling" carries sixteen identifiers — BR-MD-05-01..-12 and
 * ERR-MD-05-01..-04 — and NONE of them is enforced anywhere, deliberately.
 * They are price-LIST rules, and price lists are a proposal the specification
 * itself defers. Three independent statements say so:
 *
 *  1. MD-05 §1, immediately before every one of them: "**[NOT IN PROTOTYPE —
 *     decide at build time]** Everything from here to the end of MD-05 is a
 *     proposal for that missing layer." The marker is not scoped to a layout
 *     the way MD-11 §4's is — it says "to the end of MD-05", and all sixteen
 *     declarations sit after it and before MD-06.
 *  2. PART 0's screen table: "MD-05 | Price handling | Inventory & Warehouses
 *     | **No** — see the screen for what exists instead."
 *  3. OQ-05-01, answered by owner ruling R5: "Tiers are enough for release 1…
 *     Add price lists only when a customer needs a **dated** price, or a price
 *     for one named customer rather than for a band."
 *
 * WHAT EXISTS INSTEAD IS THIS FILE and the two records it joins. MD-05's own
 * "What exists today" table names them: tenant-defined bands here (MD-14), a
 * price per band on each item (MD-03 panel 4), a band on each partner (MD-01
 * field 26), the item's unit price as the fallback, the line-level override on
 * every document, and GEN-109's rate for a foreign currency. That IS
 * customer-specific pricing and it ships. What is still missing is a DATED
 * price change and a price for one named customer — which is precisely the
 * trigger R5 set for building the layer.
 *
 * FOUR OF THE SIXTEEN HAVE A LIVE ANALOGUE ALREADY BUILT under another
 * identifier, and saying which is the useful half of this note:
 *
 *   BR-MD-05-04  "a price must not be below zero" — every price column in the
 *                build is a non-negative CHECK already (item unit price, cost
 *                price, tier prices).
 *   BR-MD-05-07  "a price on a POSTED document must never change" — NFR-30 and
 *                GEN-111, enforced by the per-table lock triggers.
 *   BR-MD-05-10  "a user may always type over the filled-in price" — the
 *                line-level override on every document editor.
 *   BR-MD-05-08  "a price below the item's COST price must warn" — NOT built.
 *                Its nearest neighbour is MD-03 field 16a, which warns when a
 *                tier price is ABOVE the unit price. The below-cost warning is
 *                the one rule in the deferred block that would make sense
 *                without price lists at all, and it is flagged rather than
 *                built because its message (ERR-MD-05-03) is written in
 *                price-list terms and the approval it proposes ("Ask a
 *                manager") has no gate behind it yet.
 *
 * THE OTHER TWELVE HAVE NO MEANING WITHOUT THE RECORD, and are listed by
 * identifier so that anyone who greps one of them lands here instead of on an
 * empty queue row:
 *
 *   BR-MD-05-01  a list must have a name, a currency and a start date
 *   BR-MD-05-02  the end date must be on or after the start date
 *   BR-MD-05-03  a list's currency must not change once it has lines
 *   BR-MD-05-05  one item, once per list per minimum quantity
 *   BR-MD-05-06  two lists matching one customer on one day resolve by
 *                priority
 *   BR-MD-05-09  the filled-in price must be shown with where it came from
 *   BR-MD-05-11  a list any POSTED document used must not be deleted
 *   BR-MD-05-12  a list is consulted BEFORE the item's tier price
 *   ERR-MD-05-01 the end-before-start sentence
 *   ERR-MD-05-02 the duplicate item-at-quantity sentence
 *   ERR-MD-05-03 the below-cost confirmation (see BR-MD-05-08 above)
 *   ERR-MD-05-04 the currency-changed-after-lines sentence
 *
 * All twelve are BLOCKED BY THE SPEC, not missed by the build. Nothing should
 * be written against any of them until the owner takes R5's trigger — and if
 * that day comes, MD-05 §12's "sits ON TOP of tiers" is the constraint that
 * matters: a price list is consulted first and the tier price is what it falls
 * back to, so nothing in this file is replaced by it.
 */
// ── The fixed sentences of MD-14 ────────────────────────────────────────────
export const TIER_MESSAGES = {
    /** ERR-MD-14-01 / BR-MD-14-01. */
    nameRequired: 'Type a name for this tier.',
    /** ERR-MD-14-02. */
    nameTaken: (name) => `There is already a tier called ${name}.`,
    /** ERR-MD-14-04 / BR-MD-14-07 — names BOTH counts. */
    inUse: (name, customers, prices) => `${name} is on ${customers} ${customers === 1 ? 'customer' : 'customers'} and ${prices} item ${prices === 1 ? 'price' : 'prices'} and cannot be deleted. Switch it off instead.`,
    /** MD-14 §7: an inactive tier cannot be priced on a new item. */
    inactive: (name) => `${name} is switched off and cannot be priced on a new item.`,
    // ── §6 "Move customers to…" — the retire-a-tier action ────────────────────
    /** Moving a tier's customers onto itself moves nobody. */
    moveSameTier: 'Pick a different tier to move them to.',
    /** §7: an inactive tier cannot take a NEW customer — a move is exactly that. */
    moveTargetInactive: (name) => `${name} is switched off. Customers cannot be moved onto it.`,
    /** §6 hides the action at zero; the server says why in words. */
    moveNobody: (name) => `No customers are on ${name}.`,
};
/**
 * D46 — the tier family's structured refusals (the d46masters wave): key +
 * params beside the composed English sentence, so a refusal that names a
 * tier or its two in-use counts reaches an Arabic reader in Arabic. One
 * builder per parameterised member the API raises inside an HTTP exception
 * body — the raise sites throw THESE, and each `message` is composed by the
 * `TIER_MESSAGES` twin so the two cannot drift (parity pinned member by
 * member in `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `inUse` travels BOTH raw counts (customers and item prices); the
 * dictionaries pluralise each noun independently (en mirrors the sentence's
 * own two one/other ternaries, ar carries a full six-category set per
 * count). `inactive` is deliberately NOT here: no API raise site names
 * `TIER_MESSAGES.inactive` — the items service raises that wall through a
 * local RETYPED copy of the sentence (`TIER_INACTIVE` in `items.service.ts`,
 * the class the census header marks as NFR-01's territory), so a builder
 * here would be a raise shape with no raise site. `moveSameTier` and
 * `nameRequired` carry no params — the exact-match map's territory, not
 * this record's.
 */
export const TIER_REFUSALS = {
    nameTaken: (name) => ({
        message: TIER_MESSAGES.nameTaken(name),
        key: 'tiers.errNameTaken',
        params: { name },
    }),
    inUse: (name, customers, prices) => ({
        message: TIER_MESSAGES.inUse(name, customers, prices),
        key: 'tiers.errInUse',
        params: { name, customers, prices },
    }),
    moveTargetInactive: (name) => ({
        message: TIER_MESSAGES.moveTargetInactive(name),
        key: 'tiers.errMoveTargetInactive',
        params: { name },
    }),
    moveNobody: (name) => ({
        message: TIER_MESSAGES.moveNobody(name),
        key: 'tiers.errMoveNobody',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const PriceTierSchema = z
    .object({
    id: z.uuid(),
    /** TIER-### — what item prices and partners point at (BR-MD-14-05). */
    code: z.string(),
    tierNo: z.number().int().positive(),
    name: z.string(),
    /** S5 / TIER-01: optional Arabic name; the Arabic UI shows `nameAr ?? name`. */
    nameAr: z.string().nullable(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    /** BR-MD-14-03: at most one; ticking a second unticks the first. */
    isDefault: z.boolean(),
    isActive: z.boolean(),
    /** Field 7 — the first delete pre-check. */
    customersOn: z.number().int(),
    /** Field 8 — the second delete pre-check. */
    itemsPricedAt: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
export const CreatePriceTierRequestSchema = z
    .object({
    name: z.string().trim().min(1, TIER_MESSAGES.nameRequired).max(60),
    nameAr: z.string().trim().min(1).max(60).optional(),
    description: z.string().trim().max(200).optional(),
    sortOrder: z.number().int().min(0).max(999).default(0),
    isDefault: z.boolean().default(false),
})
    .strict();
export const UpdatePriceTierRequestSchema = z
    .object({
    name: z.string().trim().min(1, TIER_MESSAGES.nameRequired).max(60).optional(),
    /** null clears the Arabic name (the Arabic UI falls back to `name`). */
    nameAr: z.string().trim().min(1).max(60).nullable().optional(),
    description: z.string().trim().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    isDefault: z.boolean().optional(),
})
    .strict();
const tierParams = z.object({ tierId: z.uuid() }).strict();
export const priceTiersContract = c.router({
    list: {
        method: 'GET',
        path: '/api/price-tiers',
        query: z
            .object({
            q: z.string().optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ tiers: PriceTierSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The tier list (inventory.view), sort-order first then alphabetical. Any number of tiers (R5).',
    },
    get: {
        method: 'GET',
        path: '/api/price-tiers/:tierId',
        pathParams: tierParams,
        responses: {
            200: PriceTierSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One tier (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/price-tiers',
        body: CreatePriceTierRequestSchema,
        responses: {
            201: PriceTierSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New tier (inventory.create). Duplicate name refused (ERR-MD-14-02); a new default unticks the previous (BR-MD-14-03).',
    },
    update: {
        method: 'PATCH',
        path: '/api/price-tiers/:tierId',
        pathParams: tierParams,
        body: UpdatePriceTierRequestSchema,
        responses: {
            200: PriceTierSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a tier (inventory.edit). Renaming renames it on every item, partner and picker at once (BR-MD-14-05).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/price-tiers/:tierId/deactivate',
        pathParams: tierParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PriceTierSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch off (inventory.edit). Partners on it and stored prices stay untouched (BR-MD-14-06).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/price-tiers/:tierId/reactivate',
        pathParams: tierParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PriceTierSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (inventory.edit) — restores the stored pricing exactly.',
    },
    moveCustomers: {
        method: 'POST',
        path: '/api/price-tiers/:tierId/move-customers',
        pathParams: tierParams,
        body: z.object({ targetTierId: z.uuid() }).strict(),
        responses: {
            200: PriceTierSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'MD-14 §6 "Move customers to…": every partner on this tier moves to the target in ONE audited action, so a tier retires without visiting 12 records. Administrator (inventory.approve); the target must be a DIFFERENT, ACTIVE tier (§7: an inactive tier cannot take a new customer); refused in words when nobody is on the tier (§6 hides the action at zero).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/price-tiers/:tierId',
        pathParams: tierParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — only a tier no partner carries and no item prices (BR-MD-14-07, ERR-MD-14-04 names both counts).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=price-tiers.js.map